'use strict';

/**
 * Core (sample-set grain) customer tracker.
 *
 * Project level: an AGGREGATION of each sample set's current publication flow
 * (e.g. "2 ready · 1 publishing · 1 needs attention") — there is no permanent
 * project-level Ready; a later or republished sample set legitimately changes it.
 * Sample-set level: one linear tracker (Preparing results → Publishing → Ready)
 * for the selected sample set's CURRENT correlation only, with expandable
 * graph details.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	Collapse,
	MenuItem,
	Select,
	Stepper,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Gray } from 'metabux/theme/colors';
import { CORE_PROGRESS_COPY, CORE_PROGRESS_STAGE_IDS, PIPELINE_PROGRESS_UI_COPY } from './pipelineProgressCopy';
import { CORE_SAMPLE_SET_FLOW_STATES } from './pipelineCoreProgressModel';
import { formatGraphSpanDurationLabel } from './pipelineDuration';
import { buildCoreSampleSetDetailBundle, buildDiscoverySampleSetDetailBundle, buildKitsSampleSetDetailBundle } from './pipelineCoreProgressDetails';
import { PipelineProgressStep } from './PipelineProgressStep';
import { PipelineStageDetails } from './PipelineStageDetails';

/** @typedef {typeof CORE_PROGRESS_COPY} CoreProgressCopy */

/** Shared reset for the app-wide MuiStepper theme override (see PipelineProgressSummary). */
const STEPPER_SX = {
	mb: 1,
	'& .MuiStepLabel-iconContainer svg': {
		width: 22,
		height: 22,
		border: 'none',
		borderRadius: 0,
		color: 'inherit',
	},
	'&.MuiStepper-horizontal .MuiStepConnector-root': {
		top: '11px',
		left: 'calc(-50% + 18px)',
		right: 'calc(50% + 18px)',
	},
	'&.MuiStepper-vertical .MuiStepConnector-root': { marginLeft: '11px' },
	'&.MuiStepper-vertical .MuiStepConnector-line': {
		border: 'none',
		borderLeft: '2px solid',
		borderColor: 'grey.300',
		minHeight: 16,
	},
	'& .MuiStepLabel-label, & .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
		color: 'text.primary',
		fontSize: '0.8125rem',
	},
};

/**
 * @param {{
 *   model: import('./pipelineCoreProgressModel').CorePipelineProgressModel,
 *   facts?: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   copy?: CoreProgressCopy,
 *   testIdPrefix?: string,
 *   projectId?: string,
 *   definition?: import('./pipelineStatusTypes').PipelineStatusGraphDefinition | null,
 *   detailKind?: 'core' | 'discoveryPanels' | 'kits',
 * }} props
 */
export function CorePipelineProgressSummary({
	model,
	facts = [],
	copy = CORE_PROGRESS_COPY,
	testIdPrefix = 'core',
	projectId,
	definition = null,
	detailKind = 'core',
}) {
	const theme = useTheme();
	const narrow = useMediaQuery(theme.breakpoints.down('md'));
	const [selectedId, setSelectedId] = useState(/** @type {string | null} */ (null));
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedStepId, setSelectedStepId] = useState(/** @type {string | null} */ (null));

	const selected = useMemo(() => {
		const byId = model.sampleSets.find((s) => s.sampleSetId === selectedId);
		if (byId) return byId;
		return (
			model.sampleSets.find((s) => s.state === CORE_SAMPLE_SET_FLOW_STATES.failed)
			?? model.sampleSets.find((s) => s.state !== CORE_SAMPLE_SET_FLOW_STATES.ready)
			?? model.sampleSets[0]
		);
	}, [model.sampleSets, selectedId]);

	const detailBundle = useMemo(() => {
		if (!selected || !definition) return null;
		if (detailKind === 'discoveryPanels') return buildDiscoverySampleSetDetailBundle(selected, facts, { projectId, definition });
		if (detailKind === 'kits') return buildKitsSampleSetDetailBundle(selected, facts, { projectId, definition });
		return buildCoreSampleSetDetailBundle(selected, facts, { projectId, definition });
	}, [selected, facts, projectId, detailKind, definition]);

	const durationLabel = useMemo(() => {
		if (!selected) return undefined;
		const fromGraph = detailBundle?.stages?.length
			? formatGraphSpanDurationLabel(detailBundle.stages, selected.state)
			: undefined;
		return fromGraph || selected.durationLabel;
	}, [detailBundle, selected]);

	useEffect(() => {
		setSelectedStepId(null);
	}, [selected?.sampleSetId]);

	useEffect(() => {
		setSelectedStepId(null);
	}, [detailBundle?.currentStepId]);

	if (model.state === 'unavailable') {
		const idleSteps = Array.isArray(model.steps) ? model.steps : [];
		if (model.supported && idleSteps.length) {
			return (
				<Box
					component="section"
					aria-label={copy.panelTitle}
					data-testid={`${testIdPrefix}-progress-summary`}
					sx={{ mt: 1.5, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
				>
					<Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: Gray[600], fontSize: '1rem', mb: 1.5 }}>
						{copy.panelTitle}
					</Typography>
					<Stepper
						nonLinear
						activeStep={-1}
						orientation={narrow ? 'vertical' : 'horizontal'}
						alternativeLabel={!narrow}
						sx={STEPPER_SX}
					>
						{idleSteps.map((step) => (
							<PipelineProgressStep
								key={step.id}
								step={step}
								isCurrent={false}
								onSelect={() => {}}
								inactive
							/>
						))}
					</Stepper>
					<Typography
						variant="body2"
						sx={{ color: 'text.secondary' }}
						aria-live="polite"
						data-testid={`${testIdPrefix}-progress-message`}
					>
						{model.message || copy.emptyActivity}
					</Typography>
				</Box>
			);
		}
		const unavailableMessage = model.message || copy.unavailable;
		return (
			<Box
				component="section"
				aria-label={copy.panelTitle}
				data-testid={`${testIdPrefix}-progress-unavailable`}
				sx={{ mt: 1.5, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
			>
				<Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: Gray[600], fontSize: '1rem', mb: 1 }}>
					{copy.panelTitle}
				</Typography>
				<Typography variant="body2" sx={{ color: 'text.secondary' }} aria-live="polite">
					{unavailableMessage}
				</Typography>
			</Box>
		);
	}

	const summary = model.summary;
	const detailSteps = detailBundle?.steps ?? [];
	const detailStages = detailBundle?.stages ?? [];
	const effectiveStepId = selectedStepId ?? detailBundle?.currentStepId ?? detailSteps[0]?.id;
	const selectedStep = detailSteps.find((s) => s.id === effectiveStepId);
	const activeStepIndex = Math.max(0, detailSteps.findIndex((s) => s.id === detailBundle?.currentStepId));

	const onSelectStep = (stepId) => {
		setSelectedStepId(stepId);
		setDetailsOpen(true);
	};

	const onToggleDetails = () => {
		setDetailsOpen((open) => {
			if (!open) {
				// Prefer a stage that has sub-steps (Publishing) over Ready, which is derived.
				const withSubs = detailSteps.find(
					(s) => s.id === CORE_PROGRESS_STAGE_IDS.publishing && (s.subSteps?.length ?? 0) > 0,
				);
				if (withSubs) setSelectedStepId(withSubs.id);
			}
			return !open;
		});
	};

	return (
		<Box
			component="section"
			aria-label={copy.panelTitle}
			data-testid={`${testIdPrefix}-progress-summary`}
			sx={{ mt: 1.5, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
				<Typography variant="h5" component="h2" sx={{ fontWeight: 600, color: Gray[600], flexGrow: 1, fontSize: '1rem' }}>
					{copy.panelTitle}
				</Typography>
				{summary && (
					<Box sx={{ display: 'flex', gap: 0.75 }} data-testid={`${testIdPrefix}-progress-aggregate`}>
						{summary.ready > 0 && (
							<Chip size="small" color="success" variant="outlined" icon={<CheckCircleIcon />} label={copy.summaryReady(summary.ready)} />
						)}
						{summary.inProgress > 0 && (
							<Chip size="small" color="primary" variant="outlined" label={copy.summaryInProgress(summary.inProgress)} />
						)}
						{summary.failed > 0 && (
							<Chip size="small" color="error" variant="outlined" icon={<ErrorOutlineIcon />} label={copy.summaryFailed(summary.failed)} />
						)}
					</Box>
				)}
				<Button
					size="small"
					onClick={onToggleDetails}
					data-testid={`${testIdPrefix}-progress-details-toggle`}
					aria-label={detailsOpen ? PIPELINE_PROGRESS_UI_COPY.hideDetails : PIPELINE_PROGRESS_UI_COPY.viewDetails}
				>
					{detailsOpen ? PIPELINE_PROGRESS_UI_COPY.hideDetails : PIPELINE_PROGRESS_UI_COPY.viewDetails}
				</Button>
			</Box>

			{model.sampleSets.length > 1 && (
				<Select
					size="small"
					value={selected?.sampleSetId ?? ''}
					onChange={(e) => setSelectedId(String(e.target.value))}
					sx={{ mb: 1.5, minWidth: 340, fontFamily: 'monospace' }}
					inputProps={{ 'aria-label': copy.sampleSetListLabel }}
					data-testid={`${testIdPrefix}-progress-sample-set-select`}
				>
					{model.sampleSets.map((s) => (
						<MenuItem key={s.sampleSetId} value={s.sampleSetId}>
							{s.name}
						</MenuItem>
					))}
				</Select>
			)}

			{selected && (
				<Box data-testid={`${testIdPrefix}-progress-sample-set-${selected.sampleSetId}`}>
					{model.sampleSets.length === 1 && (
						<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontFamily: 'monospace' }}>
							{selected.name}
						</Typography>
					)}
					<Stepper
						nonLinear
						activeStep={activeStepIndex}
						orientation={narrow ? 'vertical' : 'horizontal'}
						alternativeLabel={!narrow}
						sx={STEPPER_SX}
					>
						{(detailSteps.length ? detailSteps : selected.steps).map((step) => (
							<PipelineProgressStep
								key={step.id}
								step={step}
								isCurrent={step.id === detailBundle?.currentStepId}
								onSelect={onSelectStep}
							/>
						))}
					</Stepper>
					<Typography
						variant="body2"
						sx={{ color: selected.state === CORE_SAMPLE_SET_FLOW_STATES.failed ? 'error.main' : Gray[800] }}
						aria-live="polite"
						role={selected.state === CORE_SAMPLE_SET_FLOW_STATES.failed ? 'alert' : undefined}
						data-testid={`${testIdPrefix}-progress-message`}
					>
						{selected.message}
						{durationLabel ? ` · ${durationLabel}` : ''}
					</Typography>

					<Collapse in={detailsOpen} unmountOnExit>
						{definition ? (
							<PipelineStageDetails step={selectedStep} stages={detailStages} />
						) : (
							<Alert
								severity="warning"
								sx={{ mt: 1.5 }}
								data-testid={`${testIdPrefix}-progress-details-missing-graph`}
							>
								[pipelineStatus] Sample-set details need a PortalApi graph definition.
							</Alert>
						)}
					</Collapse>
				</Box>
			)}

			{summary?.allReady && (
				<Typography variant="body2" sx={{ mt: 1, color: 'success.main' }} data-testid={`${testIdPrefix}-progress-all-ready`}>
					{copy.allReady}
					{durationLabel && selected?.state === CORE_SAMPLE_SET_FLOW_STATES.ready
						? ` · ${durationLabel}`
						: ''}
				</Typography>
			)}
		</Box>
	);
}
